import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet as WalletIcon, Plus, ArrowUpRight, ArrowDownRight, Clock, CheckCircle,
  XCircle, Copy, AlertCircle, IndianRupee, CreditCard, Landmark, Sparkles, Zap,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { load as loadCashfree } from '@cashfreepayments/cashfree-js';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Replace with real biz UPI when available; lives here so admin can update later.
const OWNER_UPI_VPA = 'rentkollect@upi';

const Wallet = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview'); // overview | transactions | topups
  const [txns, setTxns] = useState([]);
  const [topups, setTopups] = useState([]);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [gatewayInfo, setGatewayInfo] = useState(null);

  const h = { headers: { Authorization: `Bearer ${token}` } };

  const fetchWallet = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, txRes, tuRes, gwRes] = await Promise.all([
        axios.get(`${API}/wallet/me`, h),
        axios.get(`${API}/wallet/transactions?limit=50`, h),
        axios.get(`${API}/wallet/topups`, h),
        axios.get(`${API}/payments/gateway/active`),
      ]);
      setData(meRes.data);
      setTxns(txRes.data.transactions || []);
      setTopups(tuRes.data || []);
      setGatewayInfo(gwRes.data);
    } catch {
      toast({ title: 'Error loading wallet', variant: 'destructive' });
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  // Post-redirect verification: if ?cf_order_id=... present, verify & reload.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cfOrderId = params.get('cf_order_id');
    if (!cfOrderId || !token) return;
    (async () => {
      try {
        const v = await axios.get(`${API}/wallet/cashfree/verify/${cfOrderId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (v.data?.status === 'approved') {
          toast({ title: 'Payment successful', description: `₹${v.data.amount} credited to your wallet.` });
        } else {
          toast({ title: 'Payment pending', description: 'We will credit your wallet once confirmed.' });
        }
      } catch {
        toast({ title: 'Verification pending', description: 'Your wallet will update shortly.' });
      } finally {
        // Clean URL
        const url = new URL(window.location.href);
        url.searchParams.delete('cf_order_id');
        window.history.replaceState({}, '', url.toString());
        fetchWallet();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading || !data) {
    return <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const wallet = data.wallet;
  const pricing = data.pricing || {};

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="wallet-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <WalletIcon className="text-blue-600" /> Wallet & Credits
          </h1>
          <p className="text-gray-600 mt-1">Pre-paid credits for premium services like rent agreement e-stamping & Aadhaar e-sign.</p>
        </div>
        <Button
          data-testid="wallet-topup-btn"
          onClick={() => setShowTopupModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus size={16} className="mr-2" /> Top Up Wallet
        </Button>
      </div>

      {/* Hero balance card */}
      <Card className="relative overflow-hidden mb-6 border-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        <div className="relative p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            <div className="md:col-span-2">
              <p className="text-sm text-blue-100 uppercase tracking-wider">Available Balance</p>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-5xl md:text-6xl font-bold tabular-nums" data-testid="wallet-balance">{wallet.balance.toLocaleString()}</span>
                <span className="text-xl text-blue-200">credits</span>
              </div>
              <p className="text-xs text-blue-200 mt-1">= ₹{wallet.balance.toLocaleString()} (1 credit = ₹1)</p>
            </div>
            <div>
              <p className="text-xs text-blue-100 uppercase tracking-wider">Total Topped Up</p>
              <p className="text-2xl font-bold mt-1">₹{(wallet.total_topped_up || 0).toLocaleString()}</p>
              <p className="text-xs text-blue-200 mt-1 flex items-center gap-1"><ArrowUpRight size={12} /> Lifetime</p>
            </div>
            <div>
              <p className="text-xs text-blue-100 uppercase tracking-wider">Total Spent</p>
              <p className="text-2xl font-bold mt-1">₹{(wallet.total_spent || 0).toLocaleString()}</p>
              <p className="text-xs text-blue-200 mt-1 flex items-center gap-1"><ArrowDownRight size={12} /> On services</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Service pricing */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Sparkles size={18} className="text-purple-600" /> What credits are used for
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(pricing).map(([key, svc]) => (
            <Card key={key} className="p-4 hover:border-blue-300 transition" data-testid={`pricing-${key}`}>
              <div className="flex justify-between items-start mb-2">
                <p className="font-semibold text-gray-900 text-sm">{svc.label}</p>
                <span className="text-lg font-bold text-blue-700">{svc.credits}</span>
              </div>
              <p className="text-xs text-gray-500">{svc.description}</p>
            </Card>
          ))}
        </div>
      </div>

      {/* Pending top-ups */}
      {data.pending_topups?.length > 0 && (
        <Card className="p-4 mb-6 border-l-4 border-l-amber-500 bg-amber-50">
          <div className="flex items-start gap-3">
            <Clock size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900">
                {data.pending_topups.length} pending top-up{data.pending_topups.length > 1 ? 's' : ''}
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                Your Super Admin is reviewing. Credits will be added as soon as your UTR is verified.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b">
        {[
          ['overview', 'Recent Activity'],
          ['transactions', 'All Transactions'],
          ['topups', 'Top-Up History'],
        ].map(([v, label]) => (
          <button
            key={v}
            data-testid={`wallet-tab-${v}`}
            onClick={() => setTab(v)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
              tab === v ? 'border-blue-500 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <TxnTable txns={data.recent_transactions} empty="No transactions yet. Top up your wallet to get started." />
      )}
      {tab === 'transactions' && (
        <TxnTable txns={txns} empty="No transactions found." />
      )}
      {tab === 'topups' && (
        <TopupTable topups={topups} />
      )}

      {showTopupModal && (
        <TopupModal
          open={showTopupModal}
          onClose={() => setShowTopupModal(false)}
          onSuccess={() => { setShowTopupModal(false); fetchWallet(); }}
          token={token}
          gatewayInfo={gatewayInfo}
        />
      )}
    </div>
  );
};

// ---------- Transaction table ----------
const TxnTable = ({ txns, empty }) => {
  if (!txns?.length) {
    return (
      <Card className="p-10 text-center" data-testid="txns-empty">
        <WalletIcon size={40} className="mx-auto text-gray-300 mb-2" />
        <p className="text-gray-600">{empty}</p>
      </Card>
    );
  }
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <table className="w-full" data-testid="wallet-txns-table">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">When</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
            <th className="p-3 text-right text-xs font-semibold text-gray-600 uppercase">Credits</th>
            <th className="p-3 text-right text-xs font-semibold text-gray-600 uppercase">Balance After</th>
          </tr>
        </thead>
        <tbody>
          {txns.map(t => {
            const isCredit = t.type === 'credit';
            return (
              <tr key={t.id} className="border-b hover:bg-gray-50" data-testid={`txn-${t.id}`}>
                <td className="p-3 text-xs text-gray-600">{new Date(t.created_at).toLocaleString()}</td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    isCredit ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {isCredit ? <ArrowUpRight size={11}/> : <ArrowDownRight size={11}/>}
                    {isCredit ? 'Credit' : 'Debit'}
                  </span>
                </td>
                <td className="p-3 text-sm">
                  <p className="text-gray-900 font-medium">{t.note || t.reason}</p>
                  <p className="text-xs text-gray-500">{t.reason}{t.reference ? ` · ${t.reference.slice(0,8)}` : ''}</p>
                </td>
                <td className={`p-3 text-right text-sm font-semibold tabular-nums ${isCredit ? 'text-green-700' : 'text-red-700'}`}>
                  {isCredit ? '+' : '−'}{t.credits}
                </td>
                <td className="p-3 text-right text-sm text-gray-700 tabular-nums">{t.balance_after}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---------- Topup history ----------
const TopupTable = ({ topups }) => {
  if (!topups.length) {
    return (
      <Card className="p-10 text-center">
        <p className="text-gray-600">No top-up requests yet.</p>
      </Card>
    );
  }
  const statusCfg = {
    pending:  { cls: 'bg-amber-100 text-amber-800', icon: Clock },
    approved: { cls: 'bg-green-100 text-green-700', icon: CheckCircle },
    rejected: { cls: 'bg-red-100 text-red-700',     icon: XCircle },
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Submitted</th>
            <th className="p-3 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Method</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Reference</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
          </tr>
        </thead>
        <tbody>
          {topups.map(t => {
            const s = statusCfg[t.status] || statusCfg.pending;
            const Icon = s.icon;
            return (
              <tr key={t.id} className="border-b hover:bg-gray-50">
                <td className="p-3 text-xs text-gray-600">{new Date(t.created_at).toLocaleString()}</td>
                <td className="p-3 text-right text-sm font-semibold tabular-nums">₹{t.amount.toLocaleString()}</td>
                <td className="p-3 text-sm capitalize">{(t.method || '').replace('_', ' ')}</td>
                <td className="p-3 text-xs font-mono text-gray-700">{t.reference}</td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
                    <Icon size={11} /> {t.status}
                  </span>
                  {t.status === 'rejected' && t.reject_reason && (
                    <p className="text-xs text-red-600 mt-0.5">{t.reject_reason}</p>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---------- Topup Modal ----------
const TopupModal = ({ open, onClose, onSuccess, token, gatewayInfo }) => {
  const activeGw = gatewayInfo?.active || 'cashfree';
  const gwConfigured = !!gatewayInfo?.configured;
  // Default to the active gateway if it's configured, else manual
  const [mode, setMode] = useState(gwConfigured ? activeGw : 'manual');
  const [amount, setAmount] = useState(500);
  const [method, setMethod] = useState('upi');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const QUICK = [250, 500, 1000, 2500, 5000];

  const submitManual = async () => {
    if (!reference.trim() || reference.trim().length < 3) {
      toast({ title: 'Enter your UTR / transaction ID', description: 'We need this to verify the payment.', variant: 'destructive' });
      return;
    }
    if (!amount || parseInt(amount, 10) <= 0) {
      toast({ title: 'Enter an amount', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/wallet/topup/manual`, {
        amount: parseInt(amount, 10),
        method,
        reference: reference.trim(),
        note: note || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({
        title: 'Top-up submitted',
        description: `Your ₹${amount} top-up is pending Super Admin review. Credits will appear in your wallet once verified.`,
      });
      onSuccess();
    } catch (e) {
      toast({
        title: 'Submit failed',
        description: e.response?.data?.detail || 'Try again',
        variant: 'destructive',
      });
    } finally { setSaving(false); }
  };

  const tryRazorpay = async () => {
    try {
      await axios.post(`${API}/wallet/topup/razorpay/order`, { amount: parseInt(amount, 10) }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      toast({
        title: 'Razorpay coming soon',
        description: e.response?.data?.detail || 'Use Manual top-up for now.',
      });
    }
  };

  const payWithCashfree = async () => {
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) {
      toast({ title: 'Enter an amount', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const { data: order } = await axios.post(
        `${API}/wallet/cashfree/order`,
        { amount: amt, note: note || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const cashfree = await loadCashfree({ mode: order.environment === 'production' ? 'production' : 'sandbox' });
      await cashfree.checkout({
        paymentSessionId: order.payment_session_id,
        redirectTarget: '_self',
      });
      // If redirectTarget='_self', browser navigates away. Nothing to do post-call.
    } catch (e) {
      toast({
        title: 'Cashfree checkout failed',
        description: e.response?.data?.detail || e.message || 'Try again or use Manual.',
        variant: 'destructive',
      });
      setSaving(false);
    }
  };

  const copyVpa = () => {
    navigator.clipboard.writeText(OWNER_UPI_VPA);
    toast({ title: 'UPI ID copied', description: OWNER_UPI_VPA });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus size={20} className="text-blue-600" /> Top Up Wallet
          </DialogTitle>
        </DialogHeader>

        {/* Amount */}
        <div className="space-y-4">
          <div>
            <Label>Amount (₹)</Label>
            <div className="flex gap-2 mt-1 mb-2 flex-wrap">
              {QUICK.map(v => (
                <button
                  key={v}
                  data-testid={`quick-${v}`}
                  type="button"
                  onClick={() => setAmount(v)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                    amount === v
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                  }`}
                >
                  ₹{v}
                </button>
              ))}
            </div>
            <div className="relative">
              <IndianRupee size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                data-testid="topup-amount"
                type="number"
                min="1"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="pl-9 text-lg font-semibold"
                placeholder="Enter amount"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">You'll get <span className="font-semibold text-gray-800">{Number(amount || 0).toLocaleString()} credits</span> (1 credit = ₹1).</p>
          </div>

          {/* Mode switch */}
          <div className="grid grid-cols-3 gap-2">
            <button
              data-testid="mode-cashfree"
              type="button"
              onClick={() => setMode('cashfree')}
              disabled={activeGw !== 'cashfree' || !gwConfigured}
              className={`p-3 rounded-lg border text-left transition ${
                mode === 'cashfree' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-300 hover:border-blue-400'
              } ${activeGw !== 'cashfree' || !gwConfigured ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center gap-2">
                <Zap size={16} className="text-emerald-600" />
                <p className="font-semibold text-sm">Cashfree</p>
                {activeGw === 'cashfree' && gwConfigured && (
                  <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">Live</span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Instant UPI / card / netbanking.</p>
            </button>
            <button
              data-testid="mode-razorpay"
              type="button"
              onClick={() => setMode('razorpay')}
              disabled={activeGw !== 'razorpay' || !gwConfigured}
              className={`p-3 rounded-lg border text-left transition ${
                mode === 'razorpay' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-300 hover:border-blue-400'
              } ${activeGw !== 'razorpay' || !gwConfigured ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center gap-2">
                <CreditCard size={16} className="text-blue-600" />
                <p className="font-semibold text-sm">Razorpay</p>
                <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                  {activeGw === 'razorpay' ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Instant UPI / card / netbanking.</p>
            </button>
            <button
              data-testid="mode-manual"
              type="button"
              onClick={() => setMode('manual')}
              className={`p-3 rounded-lg border text-left transition ${
                mode === 'manual' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-300 hover:border-blue-400'
              }`}
            >
              <div className="flex items-center gap-2">
                <Landmark size={16} className="text-gray-600" />
                <p className="font-semibold text-sm">Manual UPI</p>
              </div>
              <p className="text-xs text-gray-500 mt-1">Pay, enter UTR, admin credits you.</p>
            </button>
          </div>

          {mode === 'cashfree' && (
            <div className="p-4 border border-emerald-200 rounded-lg bg-emerald-50/40 space-y-3">
              <div className="flex items-start gap-2">
                <Zap size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Pay instantly via Cashfree</p>
                  <p className="text-xs text-gray-600">UPI, cards, netbanking, wallets — credits land in your wallet the moment payment succeeds.</p>
                </div>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Input
                  data-testid="cashfree-note"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Purpose of top-up"
                />
              </div>
              {gatewayInfo?.cashfree_environment === 'sandbox' && (
                <div className="flex items-start gap-2 text-xs text-gray-600 bg-white p-2 rounded border border-gray-200">
                  <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  Sandbox mode — no real money moves. Use test card 4111 1111 1111 1111, OTP 111000.
                </div>
              )}
              <Button
                data-testid="topup-submit-cashfree"
                onClick={payWithCashfree}
                disabled={saving}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {saving ? 'Redirecting…' : `Pay ₹${Number(amount || 0).toLocaleString()} with Cashfree`}
              </Button>
            </div>
          )}

          {mode === 'manual' && (
            <div className="space-y-3 p-4 border border-blue-200 rounded-lg bg-blue-50/40">
              <div>
                <p className="text-sm font-semibold text-gray-800 mb-2">Step 1: Pay to our UPI</p>
                <div className="flex items-center gap-2 bg-white p-2.5 rounded-lg border">
                  <code className="flex-1 font-mono text-sm text-blue-700">{OWNER_UPI_VPA}</code>
                  <button
                    data-testid="copy-vpa-btn"
                    type="button"
                    onClick={copyVpa}
                    className="text-blue-600 hover:bg-blue-100 p-1.5 rounded"
                    title="Copy UPI ID"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Use your UPI app (GPay / PhonePe / Paytm) to pay <span className="font-semibold">₹{Number(amount || 0).toLocaleString()}</span>.
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-800 mb-2">Step 2: Submit payment details</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Payment method</Label>
                    <select
                      data-testid="topup-method"
                      value={method}
                      onChange={e => setMethod(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="upi">UPI</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cash">Cash (to office)</option>
                    </select>
                  </div>
                  <div>
                    <Label>UTR / Transaction ID *</Label>
                    <Input
                      data-testid="topup-reference"
                      value={reference}
                      onChange={e => setReference(e.target.value)}
                      placeholder="e.g., 123456789012"
                      required
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Note (optional)</Label>
                  <Input
                    data-testid="topup-note"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Any reference for the admin"
                  />
                </div>
              </div>

              <div className="flex items-start gap-2 text-xs text-gray-600 bg-white p-2 rounded border border-gray-200">
                <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                Credits land in your wallet only AFTER the Super Admin verifies the UTR. Usually takes under an hour during business hours.
              </div>

              <Button
                data-testid="topup-submit-manual"
                onClick={submitManual}
                disabled={saving}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {saving ? 'Submitting...' : `Submit Top-Up Request for ₹${Number(amount || 0).toLocaleString()}`}
              </Button>
            </div>
          )}

          {mode === 'razorpay' && (
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-center">
              <CreditCard size={32} className="mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-700 font-medium">Razorpay checkout activating soon</p>
              <p className="text-xs text-gray-500 mt-1">Once live, you'll be able to pay via UPI/card/netbanking and get credits instantly.</p>
              <Button
                data-testid="topup-try-razorpay"
                type="button"
                variant="outline"
                onClick={tryRazorpay}
                className="mt-3"
              >
                Check status
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Wallet;
